import { useEffect, useState } from 'react'
import { useT } from '@/i18n'
import { useToast } from '@/providers/UiProviders'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { ApiError } from '@/lib/api'
import { repriceListing, type AdminGromAuktionListing } from './gromAuktionApi'

export function RepriceDialog({
  listing,
  onClose,
  onSaved,
}: {
  listing: AdminGromAuktionListing | null
  onClose: () => void
  onSaved: () => void
}) {
  const t = useT()
  const { toast } = useToast()

  const [price, setPrice] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!listing) return
    setPrice(String(listing.price))
    setError(null)
  }, [listing])

  if (!listing) return null

  async function handleSubmit() {
    if (!listing) return
    setError(null)
    const priceNum = Number(price)
    if (Number.isNaN(priceNum) || priceNum < 0) {
      setError(t.gromauktion.fillRequired)
      return
    }

    setSubmitting(true)
    try {
      await repriceListing(listing.id, priceNum)
      toast(t.gromauktion.repriceSuccess, { tone: 'success' })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={Boolean(listing)}
      onClose={onClose}
      title={t.gromauktion.repriceTitle}
      description={`@${listing.username}`}
      width="sm"
      footer={
        <>
          <div className="flex-1" />
          <Button variant="default" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {t.common.save}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label={t.gromauktion.price} required>
          <Input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Dialog>
  )
}
